import { Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { AuthProvider } from './contexts/AuthContext'
import { ProtectedRoute } from './components/ProtectedRoute'
import { ToastProvider } from './components/ui/Toast'

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-100">
    <div className="spinner"></div>
  </div>
)

// Lazy loaded pages
const LoginPage = lazy(() => import('./pages/LoginPage'))
const SuperAdminPage = lazy(() => import('./pages/superadmin/SuperAdminPage'))
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'))
const DashboardPage = lazy(() => import('./pages/admin/DashboardPage'))
const ProductsPage = lazy(() => import('./pages/admin/ProductsPage'))
const ProductFormPage = lazy(() => import('./pages/admin/ProductFormPage'))
const CategoriesPage = lazy(() => import('./pages/admin/CategoriesPage'))
const WorkersPage = lazy(() => import('./pages/admin/WorkersPage'))
const WorkerFormPage = lazy(() => import('./pages/admin/WorkerFormPage'))
const ReceiptsPage = lazy(() => import('./pages/admin/ReceiptsPage'))
const ReceiptDetailPage = lazy(() => import('./pages/admin/ReceiptDetailPage'))
const SettingsPage = lazy(() => import('./pages/admin/SettingsPage'))
const ReportsPage = lazy(() => import('./pages/admin/ReportsPage'))
const TimeTrackingPage = lazy(() => import('./pages/admin/TimeTrackingPage'))
const HappyHourPage = lazy(() => import('./pages/admin/HappyHourPage'))
const StockPage = lazy(() => import('./pages/admin/StockPage'))
const POSLoginPage = lazy(() => import('./pages/pos/POSLoginPage'))
const POSCheckoutPage = lazy(() => import('./pages/pos/POSCheckoutPage'))
const OfflinePage = lazy(() => import('./pages/OfflinePage'))

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/offline" element={<OfflinePage />} />

            {/* SuperAdmin routes */}
            <Route
              path="/superadmin/*"
              element={
                <ProtectedRoute requiredRole="superadmin">
                  <SuperAdminPage />
                </ProtectedRoute>
              }
            />

            {/* Admin routes */}
            <Route
              path="/admin/:orgId"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="products/:productId" element={<ProductFormPage />} />
              <Route path="categories" element={<CategoriesPage />} />
              <Route path="workers" element={<WorkersPage />} />
              <Route path="workers/:workerId" element={<WorkerFormPage />} />
              <Route path="receipts" element={<ReceiptsPage />} />
              <Route path="receipts/:receiptId" element={<ReceiptDetailPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="time-tracking" element={<TimeTrackingPage />} />
              <Route path="happy-hour" element={<HappyHourPage />} />
              <Route path="stock" element={<StockPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>

            {/* POS routes */}
            <Route path="/pos/:orgId/login" element={<POSLoginPage />} />
            <Route path="/pos/:orgId/checkout" element={<POSCheckoutPage />} />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Suspense>
      </ToastProvider>
    </AuthProvider>
  )
}
